/**
 * AppShell: the frame the workbench views plug into.
 *
 * Layout route: `<ProtectedRoute>` hands it the signed-in user via outlet
 * context; it renders the left nav rail (wordmark + sectioned nav), the top bar
 * (title + theme toggle + user menu), and forwards the user to the page `<Outlet>`.
 *
 * Nav note: the rail is a persistent, active-route side nav, not a flyout
 * mega-menu, so its leaf items are router `<Link>`s styled with DS token
 * utilities (the DS `NavigationMenu` compound is built for flyout panels; the
 * frontend rules sanction router links for a plain side/top nav). Below `md` the
 * rail is hidden and a DS `Menu` in the top bar navigates instead: it is built from
 * the same capability-filtered sections as the rail and also holds Give feedback and
 * the theme switch, so the header fits a 390 px phone without a horizontal scroll. At
 * `md` and above the rail is the only navigation.
 *
 * Groups (the product owner, 15 Oct 2026): "Work" is always open. "Insight", "Learn and
 * help", "Plans and decisions" and "Feedback" are DS `Collapsible` groups with a header
 * button; a group is closed until the viewer opens it, except the group of the page that
 * is open. A group with no entry for the viewer is not shown. The toggle at the bottom
 * turns the rail into an icon strip (about 64 px) with a tooltip per icon and a dot for a
 * badge. The open groups and the strip are kept in `localStorage` (`NAV_STORAGE_KEY`);
 * every read and write is in try/catch, and the rail works without it. The phone menu
 * lists the same groups.
 *
 * The rail keeps a fixed navy surface in both themes (brand tokens, not the
 * theme-reactive inverse pair), so the white Norican mark stays visible in dark mode.
 *
 * "Back for you" lists the cases that came back to the viewer (a customer they confirmed
 * was matched again, a case waiting for the customer for 5 working days); its badge is
 * `GET /me/attention`, read at most once a minute. These reminders exist only in the
 * app: no mail and no Teams message is sent. Admins also get "Team" (the team part of the
 * Operations tab), "Training uptake", "Usage" and "Cost and runs".
 *
 * An executive-only session (Role.Executive without Role.Manager or Role.Admin, Timo 10
 * Oct 2026) does no sales work: the rail and the phone menu show the entries marked
 * `executive` only, the logo leads to the dashboard, and the queue count and "Back for
 * you" are not asked for (the server answers 403 to such a session on those routes).
 *
 * The page area clips sideways instead of scrolling (`overflow-x-clip`), so the case
 * page's work bar and the queue's action bar can stay in view while the page scrolls;
 * wide tables scroll inside their own card.
 */

import {
  Activity,
  Bell,
  BookOpen,
  BookText,
  ChartColumn,
  ChevronDown,
  CircleQuestionMark,
  Database,
  Gauge,
  GraduationCap,
  Icon,
  type IconComponent,
  LayoutGrid,
  ListChecks,
  LogOut,
  MenuIcon,
  MessageCircle,
  MessageSquarePlus,
  MessagesSquare,
  Milestone,
  MonitorCheck,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Presentation,
  ReceiptText,
  RotateCcw,
  Scale,
  Sun,
  UsersRound,
  Workflow,
} from "@norican/icons";
import {
  Avatar,
  Badge,
  Button,
  cn,
  Collapsible,
  Menu,
  Spinner,
  TextLink,
  Tooltip,
  Typography,
} from "@norican/react";
import { useIsFetching } from "@tanstack/react-query";
import { Suspense, useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate, useOutletContext } from "react-router";

import { EngineStatusBanner } from "@/components/engine-status-banner";
import { NoricanLogo } from "@/components/norican-logo";
import { ShadowModeBadge } from "@/components/shadow-mode-badge";
import { useAppTheme } from "@/contexts/theme";
import { GiveFeedbackButton, type GiveFeedbackOptions, useGiveFeedback } from "@/features/feedback";
import {
  BACK_FOR_YOU_HREF,
  defaultAssigneeFor,
  defaultQueueFilters,
  queueBadgeText,
  queueHref,
  readRememberedMailboxes,
} from "@/features/queue/queue-defaults";
import { RestartTrainingDialog } from "@/features/training/restart-dialog";
import {
  type LiveUserProfile as UserProfile,
  useFeedbackUnreadCount,
  useLogout,
  useMyAttention,
  useQueue,
  useTrainingSummary,
} from "@/lib/api";
import { hasCapability, isExecutiveOnly, useMyAreas } from "@/lib/areas";
import { FEATURES } from "@/lib/features";
import { logger } from "@/lib/logger";
import { PRODUCT_NAME, PRODUCT_SHORT } from "@/lib/product";

interface NavItem {
  label: string;
  icon: IconComponent;
  path: string;
  /** Show the number of cases waiting for a reviewer next to the item. */
  reviewCount?: boolean;
  /** Show the number of own feedback items with news next to the item. */
  unreadFeedback?: boolean;
  /** Shown only to sessions holding this capability (the server stays the gate). */
  capability?: string;
  /** Link target when it is more than the path (a filter or an anchor). */
  href?: string;
  /** Active only while the address has this query part or hash (e.g. `attention=back_for_me`). */
  activeWhen?: string;
  /** Show the number of cases back for the viewer next to the item. */
  backForYou?: boolean;
  /** Also shown to an executive-only session (everything else is hidden for it). */
  executive?: boolean;
  /** Show "New" until the sales training is completed. */
  trainingBadge?: boolean;
}

/** The groups of the rail and the phone menu, in display order (labels of record). */
/** The skip link of the shell (pm9, UX M9). */
export const SKIP_TO_CONTENT = "Skip to content";

export const NAV_GROUP_LABELS = {
  work: "Work",
  insight: "Insight",
  learn: "Learn and help",
  plans: "Plans and decisions",
  feedback: "Feedback",
} as const;

export type NavGroupId = keyof typeof NAV_GROUP_LABELS;

/** The one toggle at the bottom of the rail (labels of record). */
export const NAV_TOGGLE_LABELS = {
  collapse: "Collapse menu",
  expand: "Expand menu",
} as const;

/** Where the rail remembers its open groups and its collapsed state (this browser only). */
export const NAV_STORAGE_KEY = "i2q.nav";

interface NavSection {
  id: NavGroupId;
  heading: string;
  /** False for "Work": always open, no header button. */
  collapsible: boolean;
  items: NavItem[];
}

const NAV_SECTIONS: NavSection[] = [
  {
    id: "work",
    heading: NAV_GROUP_LABELS.work,
    collapsible: false,
    items: [
      { label: "Queue", icon: LayoutGrid, path: "/queue", reviewCount: true },
      ...(FEATURES.backForYou
        ? [
            {
              label: "Back for you",
              icon: Bell,
              path: "/queue",
              href: BACK_FOR_YOU_HREF,
              activeWhen: "attention=back_for_me",
              backForYou: true,
            },
          ]
        : []),
      { label: "Learned corrections", icon: GraduationCap, path: "/learned" },
      // What sales quoted in F&O and what I2Q learns from it (20 Oct 2026): managers and
      // admins. Not for an executive-only session: the server answers 403 to it.
      {
        label: "Learn from sales",
        icon: ReceiptText,
        path: "/learn-from-sales",
        capability: "see_sales_truth",
      },
    ],
  },
  {
    id: "insight",
    heading: NAV_GROUP_LABELS.insight,
    collapsible: true,
    items: [
      { label: "Dashboard", icon: ChartColumn, path: "/dashboard", executive: true },
      // What runs today, live from the system (15 Oct 2026): every signed-in person, an
      // executive-only session too. The server scopes it to the areas of the caller.
      { label: "What runs today", icon: MonitorCheck, path: "/platform", executive: true },
      // The master-data records that hurt cases: every signed-in person sees and marks those
      // of the own areas (15 Oct 2026). Not for an executive-only session: the server
      // answers 403 to it.
      { label: "Master data", icon: Database, path: "/master-data" },
      // How a quotation is made today, before I2Q (15 Oct 2026): only the role
      // processmining. Not for an executive-only session: the server answers 403 to it.
      {
        label: "Process mining",
        icon: Workflow,
        path: "/process-mining",
        capability: "see_process_mining",
      },
      // Team, Training uptake, Usage, Cost and runs: admins only (the product owner, 15 Oct
      // 2026). Team is the team part of the Operations tab (open cases by age, the
      // workload); active only on its own link (#team).
      {
        label: "Team",
        icon: UsersRound,
        path: "/dashboard",
        href: "/dashboard?tab=operations#team",
        activeWhen: "#team",
        capability: "see_team",
      },
      ...(FEATURES.training
        ? [
            {
              label: "Training uptake",
              icon: ListChecks,
              path: "/training/team",
              capability: "see_team",
            },
          ]
        : []),
      // Who uses I2Q, how, and where people stop.
      { label: "Usage", icon: Activity, path: "/usage", capability: "see_usage" },
      // What the model calls cost, the mail import runs and the model health.
      { label: "Cost and runs", icon: Gauge, path: "/cost", capability: "see_finops" },
    ],
  },
  {
    id: "learn",
    heading: NAV_GROUP_LABELS.learn,
    collapsible: true,
    items: [
      ...(FEATURES.training
        ? [{ label: "Training", icon: Presentation, path: "/training", trainingBadge: true }]
        : []),
      { label: "Help", icon: CircleQuestionMark, path: "/help", executive: true },
      // For everyone; managers and admins also find the live settings there.
      { label: "Rules and countries", icon: BookOpen, path: "/rules" },
      // How I2Q is built, for developers and architects (every role, 12 Oct 2026).
      { label: "Documentation", icon: BookText, path: "/docs", executive: true },
    ],
  },
  {
    id: "plans",
    heading: NAV_GROUP_LABELS.plans,
    collapsible: true,
    items: [
      // What is live, in shadow, built but off, or planned; links to the discussions.
      { label: "Roadmap", icon: Milestone, path: "/roadmap", executive: true },
      // The decisions the business takes before the go-live, by milestone (15 Oct 2026):
      // for everyone signed in; a manager records them.
      { label: "Business decisions", icon: Scale, path: "/decisions", executive: true },
      // Open questions and decisions of record; comments carry the author's name.
      { label: "Discussions", icon: MessageCircle, path: "/discussions", executive: true },
    ],
  },
  {
    id: "feedback",
    heading: NAV_GROUP_LABELS.feedback,
    collapsible: true,
    items: [
      { label: "My feedback", icon: MessagesSquare, path: "/feedback", unreadFeedback: true },
      {
        label: "Feedback triage",
        icon: ListChecks,
        path: "/feedback/triage",
        capability: "triage_feedback",
      },
    ],
  },
];

/**
 * The sections a session may see: entries that need a capability are hidden without it,
 * an executive-only session sees the entries marked `executive` only, and a group with no
 * entry left is not shown.
 */
export function visibleSections(me: UserProfile | undefined): NavSection[] {
  const executiveOnly = isExecutiveOnly(me);
  return NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter(
      (item) =>
        (!item.capability || hasCapability(me, item.capability)) &&
        (!executiveOnly || item.executive === true),
    ),
  })).filter((section) => section.items.length > 0);
}

/** What the rail remembers: the groups opened or closed by hand, and the icon strip. */
export interface NavState {
  /** A group the viewer opened (true) or closed (false); absent: the default. */
  groups: Partial<Record<NavGroupId, boolean>>;
  /** The rail shows the icon strip only. */
  collapsed: boolean;
}

const NAV_STATE_DEFAULT: NavState = { groups: {}, collapsed: false };

/** The stored rail state; the default when the storage is missing, throws or holds junk. */
export function readNavState(): NavState {
  try {
    const raw = globalThis.localStorage?.getItem(NAV_STORAGE_KEY);
    if (!raw) {
      return NAV_STATE_DEFAULT;
    }
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) {
      return NAV_STATE_DEFAULT;
    }
    const { groups, collapsed } = parsed as { groups?: unknown; collapsed?: unknown };
    const known: Partial<Record<NavGroupId, boolean>> = {};
    if (typeof groups === "object" && groups !== null) {
      for (const [id, open] of Object.entries(groups)) {
        if (id in NAV_GROUP_LABELS && typeof open === "boolean") {
          known[id as NavGroupId] = open;
        }
      }
    }
    return { groups: known, collapsed: collapsed === true };
  } catch (error) {
    logger.debug("[nav] stored state not readable", error);
    return NAV_STATE_DEFAULT;
  }
}

/** Store the rail state; a storage that throws keeps the state for this page only. */
export function writeNavState(state: NavState): void {
  try {
    globalThis.localStorage?.setItem(NAV_STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    logger.debug("[nav] state not stored", error);
  }
}

/** The group that holds the page at the address, or null. */
export function activeGroupId(sections: NavSection[], where: Where | string): NavGroupId | null {
  return (
    sections.find((section) => section.items.some((item) => isActive(item, where)))?.id ?? null
  );
}

/**
 * Whether a group shows its entries: "Work" always; another group as the viewer left it,
 * else open only when it holds the page at the address.
 */
export function isGroupOpen(
  section: NavSection,
  state: NavState,
  active: NavGroupId | null,
): boolean {
  return !section.collapsible || (state.groups[section.id] ?? section.id === active);
}

/** Where the logo leads: the queue, or the dashboard Summary for an executive-only session. */
export function homeHref(me: UserProfile | undefined): string {
  return isExecutiveOnly(me) ? "/dashboard?tab=summary" : queueHref();
}

/** The address parts that decide which nav item is active. */
interface Where {
  pathname: string;
  search: string;
  hash: string;
}

/** True when the address is a narrower view (`activeWhen`) that another item owns. */
function ownedByNarrowerItem(item: NavItem, where: Where): boolean {
  return NAV_SECTIONS.flatMap((section) => section.items).some(
    (other) =>
      other !== item &&
      other.path === item.path &&
      other.activeWhen != null &&
      item.activeWhen == null &&
      (where.search.includes(other.activeWhen) || where.hash === other.activeWhen),
  );
}

/** True when `item` is the page at the address (a case page belongs to the queue). */
/**
 * What the shell's "Give feedback" (header button, mobile menu) hands the dialog: on a
 * case page the case itself, so the feedback keeps the case id, as the testers' mail
 * promises (tester round 5, 12 Oct 2026); elsewhere the app.
 */
export function shellFeedbackOptions(pathname: string): GiveFeedbackOptions {
  const match = /^\/case\/([^/?#]+)/.exec(pathname);
  if (match?.[1]) {
    return { surface: "case", caseId: decodeURIComponent(match[1]) };
  }
  return { surface: "shell" };
}

function isActive(item: NavItem, where: Where | string): boolean {
  const at: Where = typeof where === "string" ? { pathname: where, search: "", hash: "" } : where;
  const { pathname } = at;
  if (item.activeWhen != null) {
    return (
      pathname.startsWith(item.path) &&
      (at.search.includes(item.activeWhen) || at.hash === item.activeWhen)
    );
  }
  if (ownedByNarrowerItem(item, at)) {
    return false;
  }
  return (
    (pathname.startsWith(item.path) &&
      !(item.path === "/feedback" && pathname.startsWith("/feedback/triage"))) ||
    (item.path === "/queue" && pathname.startsWith("/case"))
  );
}

/** Where a nav item leads: the last queue view for Queue, else its own target. */
function targetOf(item: NavItem): string {
  if (item.href) {
    return item.href;
  }
  return item.path === "/queue" ? queueHref() : item.path;
}

const nf = new Intl.NumberFormat(undefined);

/** "anna-maria.jensen@norican.com" → "Anna-Maria Jensen". */
function nameFromEmail(email: string): string {
  const local = email.split("@")[0] ?? "";
  const words = local
    .split(/[._]+/)
    .filter(Boolean)
    .map((word) =>
      word
        .split("-")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join("-"),
    );
  return words.join(" ") || email;
}

function displayName(me: UserProfile): string {
  const full = [me.first_name, me.last_name]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" ");
  return full || nameFromEmail(me.email);
}

function deriveInitials(me: UserProfile): string {
  const words = displayName(me).split(/\s+/).filter(Boolean);
  const first = words[0]?.charAt(0) ?? "";
  const last = words.length > 1 ? (words[words.length - 1]?.charAt(0) ?? "") : "";
  return (first + last).toUpperCase() || me.email.slice(0, 2).toUpperCase();
}

function ThemeToggle() {
  const { theme, toggleTheme } = useAppTheme();
  const next = theme === "dark" ? "light" : "dark";
  return (
    <Button
      variant="ghost"
      tone="neutral"
      iconOnly
      aria-label={`Switch to ${next} theme`}
      onClick={toggleTheme}
      startIcon={<Icon icon={theme === "dark" ? Sun : Moon} />}
    />
  );
}

/**
 * The small-screen navigation (below `md`, where the rail is hidden): one menu with the
 * rail's sections (capability-filtered), Give feedback and the theme switch. The
 * trigger names the page that is open.
 */
function MobileNav({
  sections,
  pathname,
  reviewCount,
  backCount = null,
  trainingNew = false,
}: {
  sections: NavSection[];
  pathname: Where | string;
  reviewCount: string | null;
  backCount?: number | null;
  trainingNew?: boolean;
}) {
  const navigate = useNavigate();
  const give = useGiveFeedback();
  const { theme, toggleTheme } = useAppTheme();
  const nextTheme = theme === "dark" ? "light" : "dark";
  const current = sections
    .flatMap((section) => section.items)
    .find((item) => isActive(item, pathname));

  return (
    <Menu.Root>
      <Menu.Trigger
        aria-label={current ? `Menu, now on ${current.label}` : "Menu"}
        className="hover:bg-bg-inset text-fg flex min-w-0 cursor-pointer items-center gap-1.5 rounded-md px-2 py-1 text-sm font-semibold"
      >
        <Icon icon={MenuIcon} size="sm" className="shrink-0" />
        <span className="min-w-0 truncate">{current?.label ?? "Menu"}</span>
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner align="start" sideOffset={6}>
          <Menu.Popup>
            {sections.map((section, index) => (
              <Menu.Group key={section.heading}>
                {index > 0 && <Menu.Separator />}
                <Menu.GroupLabel>{section.heading}</Menu.GroupLabel>
                {section.items.map((item) => (
                  <Menu.Item
                    key={item.path}
                    onClick={() => {
                      logger.debug("[nav] mobile menu", { to: item.path });
                      navigate(targetOf(item));
                    }}
                  >
                    <span
                      className={cn(
                        "inline-flex items-center gap-2",
                        isActive(item, pathname) && "font-bold",
                      )}
                    >
                      <Icon icon={item.icon} size="sm" />
                      {item.label}
                      {item.reviewCount && reviewCount != null && (
                        <span className="text-fg-muted text-xs tabular-nums">{reviewCount}</span>
                      )}
                      {item.backForYou && backCount != null && backCount > 0 && (
                        <span className="text-fg-muted text-xs tabular-nums">
                          ({nf.format(backCount)})
                        </span>
                      )}
                      {item.trainingBadge && trainingNew && (
                        <Badge tone="accent" size="xs">
                          New
                        </Badge>
                      )}
                    </span>
                  </Menu.Item>
                ))}
              </Menu.Group>
            ))}
            <Menu.Separator />
            {give && (
              <Menu.Item
                onClick={() =>
                  give.open(
                    shellFeedbackOptions(
                      typeof pathname === "string" ? pathname : pathname.pathname,
                    ),
                  )
                }
              >
                <span className="inline-flex items-center gap-2">
                  <Icon icon={MessageSquarePlus} size="sm" />
                  Give feedback
                </span>
              </Menu.Item>
            )}
            <Menu.Item onClick={toggleTheme}>
              <span className="inline-flex items-center gap-2">
                <Icon icon={theme === "dark" ? Sun : Moon} size="sm" />
                Switch to {nextTheme} theme
              </span>
            </Menu.Item>
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}

/** The signed-in user: avatar, name (truncated) and a menu with the areas and Sign out. */
function UserChip({ me }: { me: UserProfile }) {
  const navigate = useNavigate();
  const logout = useLogout();
  const name = displayName(me);
  const areas = useMyAreas();
  const [restarting, setRestarting] = useState(false);
  const training = FEATURES.training && !isExecutiveOnly(me);

  const signOut = () => {
    logger.debug("[auth] sign out");
    logout.mutate(undefined, {
      // Leave the workbench either way; the login page re-checks the session.
      onSettled: () => navigate("/login", { replace: true }),
    });
  };

  return (
    <Menu.Root>
      <Menu.Trigger
        aria-label={`Account menu for ${name}`}
        className="hover:bg-bg-inset flex max-w-56 cursor-pointer items-center gap-2.5 rounded-md px-1.5 py-1 text-left"
      >
        <Avatar.Root size="sm">
          <Avatar.Fallback>{deriveInitials(me)}</Avatar.Fallback>
        </Avatar.Root>
        <span className="hidden min-w-0 leading-tight sm:block">
          <span className="text-fg block truncate text-xs font-bold" title={name}>
            {name}
          </span>
          <span className="text-fg-muted block truncate text-xs" title={me.email}>
            {me.email}
          </span>
        </span>
        <Icon icon={ChevronDown} size="xs" className="text-fg-subtle shrink-0" />
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner align="end" sideOffset={6}>
          <Menu.Popup>
            {areas.label ? (
              <>
                <Menu.Group>
                  <Menu.GroupLabel>{areas.label}</Menu.GroupLabel>
                </Menu.Group>
                <Menu.Separator />
              </>
            ) : null}
            <Menu.Item
              onClick={() => {
                logger.debug("[help] open from user menu");
                navigate("/help");
              }}
            >
              <span className="inline-flex items-center gap-2">
                <Icon icon={CircleQuestionMark} size="sm" />
                Help
              </span>
            </Menu.Item>
            <Menu.Item
              onClick={() => {
                logger.debug("[feedback] open my feedback from user menu");
                navigate("/feedback");
              }}
            >
              <span className="inline-flex items-center gap-2">
                <Icon icon={MessagesSquare} size="sm" />
                My feedback
              </span>
            </Menu.Item>
            {training && (
              <Menu.Item onClick={() => setRestarting(true)}>
                <span className="inline-flex items-center gap-2">
                  <Icon icon={RotateCcw} size="sm" />
                  Restart training
                </span>
              </Menu.Item>
            )}
            <Menu.Item onClick={signOut} disabled={logout.isPending}>
              <span className="inline-flex items-center gap-2">
                <Icon icon={LogOut} size="sm" />
                Sign out
              </span>
            </Menu.Item>
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
      {training && <RestartTrainingDialog open={restarting} onOpenChange={setRestarting} />}
    </Menu.Root>
  );
}

/** What a page that loads on demand shows while its script file arrives. */
export function PageLoading() {
  return (
    <div className="flex min-h-40 items-center justify-center" data-page-loading>
      <Spinner label="Loading the page" />
    </div>
  );
}

/** The longest the sidebar counts wait for the page before they are asked anyway. */
export const SHELL_COUNTS_MAX_WAIT_MS = 4000;

/**
 * Whether the sidebar counts may be asked (ease round, 13 Oct 2026). The queue count and
 * "Back for you" each take about a second on the server. They wait until the page in the
 * shell has painted and its own requests have answered, so they never run in front of
 * `GET /cases/{id}`. After `SHELL_COUNTS_MAX_WAIT_MS` they start whatever the page does.
 */
export function shellCountsMayStart(facts: {
  /** The browser was idle once after the first paint of the shell. */
  painted: boolean;
  /** Requests in flight (`useIsFetching`). */
  fetching: number;
  /** The longest wait is over. */
  waitedOut: boolean;
}): boolean {
  return facts.waitedOut || (facts.painted && facts.fetching === 0);
}

/** True from the moment `shellCountsMayStart` held once; it never turns false again. */
function useShellCountsReady(): boolean {
  const fetching = useIsFetching();
  const [painted, setPainted] = useState(false);
  const [waitedOut, setWaitedOut] = useState(false);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setWaitedOut(true), SHELL_COUNTS_MAX_WAIT_MS);
    // After the first paint: the page's own requests are on their way by then.
    if (typeof window.requestIdleCallback === "function") {
      const idle = window.requestIdleCallback(() => setPainted(true), { timeout: 1000 });
      return () => {
        window.clearTimeout(timer);
        window.cancelIdleCallback(idle);
      };
    }
    const frame = window.setTimeout(() => setPainted(true), 200);
    return () => {
      window.clearTimeout(timer);
      window.clearTimeout(frame);
    };
  }, []);
  // Latched during render (no effect): the first time the rule holds, the counts start.
  if (!ready && shellCountsMayStart({ painted, fetching, waitedOut })) {
    setReady(true);
  }
  useEffect(() => {
    if (ready) {
      logger.debug("[shell] sidebar counts start");
    }
  }, [ready]);
  return ready;
}

/** The counts and badges the rail shows beside its entries. */
interface NavBadges {
  reviewCount: string | null;
  backCount: number | null;
  unreadFeedback: number | null | undefined;
  trainingNew: boolean;
}

/** True when an entry carries a badge (a count above 0 or "New"); the strip shows a dot. */
function hasBadge(item: NavItem, badges: NavBadges): boolean {
  return Boolean(
    (item.backForYou && badges.backCount != null && badges.backCount > 0) ||
    (item.unreadFeedback && badges.unreadFeedback != null && badges.unreadFeedback > 0) ||
    (item.trainingBadge && badges.trainingNew),
  );
}

/** One entry of the open rail: icon, label and its count or badge. */
function RailLink({ item, active, badges }: { item: NavItem; active: boolean; badges: NavBadges }) {
  const { reviewCount, backCount, unreadFeedback, trainingNew } = badges;
  return (
    <Link
      to={targetOf(item)}
      aria-current={active ? "page" : undefined}
      className={cn(
        "flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-white transition-colors",
        active ? "bg-white/15 font-bold" : "font-normal hover:bg-white/10",
      )}
    >
      <Icon icon={item.icon} size="sm" />
      <span className={cn("min-w-0 truncate", item.reviewCount ? "shrink-0" : "flex-1")}>
        {item.label}
      </span>
      {item.reviewCount && reviewCount != null && (
        // The label keeps its width; a long count ("134 new · 653 open") wraps
        // onto a second line instead of cutting "Queue" to "Qu..." (invitee review).
        <span
          className="min-w-0 flex-1 text-right text-xs leading-tight tabular-nums"
          title={reviewCount}
        >
          {reviewCount}
        </span>
      )}
      {item.backForYou && backCount != null && backCount > 0 && (
        <Badge tone="accent" size="xs" aria-label={`${backCount} back for you`}>
          {nf.format(backCount)}
        </Badge>
      )}
      {item.unreadFeedback && unreadFeedback != null && unreadFeedback > 0 && (
        <Badge tone="accent" size="xs" aria-label={`${unreadFeedback} with news`}>
          {nf.format(unreadFeedback)}
        </Badge>
      )}
      {item.trainingBadge && trainingNew && (
        <Badge tone="accent" size="xs" data-training-new>
          New
        </Badge>
      )}
    </Link>
  );
}

/** One entry of the icon strip: the icon, the label as its name and tooltip, a badge dot. */
function StripLink({
  item,
  active,
  badges,
}: {
  item: NavItem;
  active: boolean;
  badges: NavBadges;
}) {
  const badge = hasBadge(item, badges);
  return (
    <Tooltip.Root>
      <Tooltip.Trigger
        render={
          <Link
            to={targetOf(item)}
            aria-current={active ? "page" : undefined}
            aria-label={badge ? `${item.label}, new` : item.label}
            data-strip-item={item.label}
            className={cn(
              "relative flex items-center justify-center rounded-md p-2.5 text-white transition-colors",
              active ? "bg-white/15" : "hover:bg-white/10",
            )}
          />
        }
      >
        <Icon icon={item.icon} size="sm" />
        {badge && (
          <span
            aria-hidden="true"
            data-strip-dot=""
            className="bg-accent absolute top-1.5 right-1.5 size-2 rounded-full"
          />
        )}
      </Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Positioner side="right">
          <Tooltip.Popup>{item.label}</Tooltip.Popup>
        </Tooltip.Positioner>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}

/**
 * The rail at `md` and above. "Work" is always open; every other group has a header
 * button and is closed until the viewer opens it, except the group of the page that is
 * open. The toggle at the bottom turns the rail into an icon strip of about 64 px. The
 * open groups and the strip are remembered in this browser (`NAV_STORAGE_KEY`).
 */
export function NavRail({
  me,
  sections,
  where,
  badges,
  initialState,
}: {
  me: UserProfile;
  sections: NavSection[];
  where: Where;
  badges: NavBadges;
  /** Tests only: the state to start from instead of the stored one. */
  initialState?: NavState;
}) {
  const [state, setState] = useState<NavState>(() => initialState ?? readNavState());
  const active = activeGroupId(sections, where);
  const update = (next: NavState) => {
    setState(next);
    writeNavState(next);
  };
  // A group that the viewer closed by hand opens again when a page of it opens.
  useEffect(() => {
    if (active == null) {
      return;
    }
    setState((previous) => {
      if (previous.groups[active] !== false) {
        return previous;
      }
      const groups = { ...previous.groups };
      delete groups[active];
      const next = { ...previous, groups };
      writeNavState(next);
      return next;
    });
  }, [active]);
  const collapsed = state.collapsed;

  return (
    <aside
      className="border-brand-blue-700 bg-brand-blue-700 hidden w-[232px] shrink-0 flex-col gap-1 border-r px-4 py-5 text-white data-[nav-rail=strip]:w-16 data-[nav-rail=strip]:items-center data-[nav-rail=strip]:px-2 md:flex print:hidden"
      data-nav-rail={collapsed ? "strip" : "open"}
    >
      <Link
        to={homeHref(me)}
        aria-label={PRODUCT_NAME}
        className={cn(
          "flex flex-col gap-2 pt-1 pb-6 no-underline",
          collapsed ? "items-center px-0" : "items-start px-2",
        )}
      >
        {/* Secondary logo (no tagline, guide p.14): the primary needs a 54 px box (35 px
            of artwork, guide p.17), too large for a persistent rail. 24 px box = 13 px of
            artwork. */}
        <NoricanLogo kind="secondary" variant="white" height={collapsed ? 20 : 24} />
        {/* All rail text is plain White (p.21). Hierarchy comes from size and weight only
            (Manrope Regular or Bold, p.23); no opacity blends, which produce off-palette
            greys. The product mark is not repeated here: beside the name it read double
            (the product owner, 15 Oct 2026); it stays on the sign-in page. */}
        <span className="text-xs font-bold text-white">{PRODUCT_SHORT}</span>
      </Link>

      <nav aria-label="Main menu" className="flex min-h-0 w-full flex-1 flex-col gap-0.5">
        {collapsed
          ? sections.map((section, index) => (
              <div
                key={section.id}
                role="group"
                aria-label={section.heading}
                className={cn(
                  "flex flex-col items-center gap-0.5",
                  index > 0 && "mt-2 border-t border-white/20 pt-2",
                )}
              >
                {section.items.map((item) => (
                  <StripLink
                    key={item.label}
                    item={item}
                    active={isActive(item, where)}
                    badges={badges}
                  />
                ))}
              </div>
            ))
          : sections.map((section) =>
              section.collapsible ? (
                <Collapsible.Root
                  key={section.id}
                  open={isGroupOpen(section, state, active)}
                  onOpenChange={(open) => {
                    logger.debug("[nav] group", { group: section.id, open });
                    update({ ...state, groups: { ...state.groups, [section.id]: open } });
                  }}
                  // The DS root is a card (surface, border, radius); on the navy rail it is a
                  // plain group, so the card look is cleared. The DS trigger brings its own
                  // chevron (currentColor, turned when open).
                  className="flex flex-col gap-0.5 rounded-none border-0 bg-transparent"
                  data-nav-group={section.id}
                >
                  <Collapsible.Trigger className="flex w-full cursor-pointer items-center justify-between rounded-md px-2 pt-4 pb-1.5 text-left text-white hover:bg-white/10 active:bg-white/10">
                    <Typography as="span" variant="micro" className="font-bold text-white">
                      {section.heading}
                    </Typography>
                  </Collapsible.Trigger>
                  <Collapsible.Panel className="flex flex-col gap-0.5">
                    {section.items.map((item) => (
                      <RailLink
                        key={item.label}
                        item={item}
                        active={isActive(item, where)}
                        badges={badges}
                      />
                    ))}
                  </Collapsible.Panel>
                </Collapsible.Root>
              ) : (
                <div key={section.id} className="flex flex-col gap-0.5" data-nav-group={section.id}>
                  <Typography variant="micro" className="px-2 pt-4 pb-1.5 font-bold text-white">
                    {section.heading}
                  </Typography>
                  {section.items.map((item) => (
                    <RailLink
                      key={item.label}
                      item={item}
                      active={isActive(item, where)}
                      badges={badges}
                    />
                  ))}
                </div>
              ),
            )}
      </nav>

      <Button
        variant="ghost"
        tone="neutral"
        size="sm"
        iconOnly={collapsed}
        aria-label={collapsed ? NAV_TOGGLE_LABELS.expand : NAV_TOGGLE_LABELS.collapse}
        data-nav-toggle=""
        className={cn("mt-4 text-white hover:bg-white/10", collapsed ? "" : "justify-start")}
        startIcon={<Icon icon={collapsed ? PanelLeftOpen : PanelLeftClose} size="sm" />}
        onClick={() => {
          logger.debug("[nav] rail", { collapsed: !collapsed });
          update({ ...state, collapsed: !collapsed });
        }}
      >
        {collapsed ? null : NAV_TOGGLE_LABELS.collapse}
      </Button>
    </aside>
  );
}

export function AppShell() {
  const { me } = useOutletContext<{ me: UserProfile }>();
  const location = useLocation();
  const executiveOnly = isExecutiveOnly(me);
  // The two slow counts of the rail start after the page content (see `shellCountsMayStart`).
  const countsReady = useShellCountsReady();

  // The number of cases waiting for a reviewer under the queue's default view (mine and
  // unassigned, received today, my default mailboxes): the same filters the queue asks
  // for on a fresh visit (`defaultQueueFilters`), so the count is what the "To review"
  // tab shows on first open. The start day is a date string, so the query key changes
  // only when the day does. One row is requested, only the count is used.
  const { data: reviewPage } = useQueue(
    {
      ...defaultQueueFilters(
        {
          scopeMailboxes: me?.mailboxes ?? [],
          remembered: readRememberedMailboxes(),
          // managers open the queue on "Everyone", so the badge counts the same rows
          assignee: defaultAssigneeFor(me?.roles),
        },
        new Date(),
      ),
      limit: 1,
    },
    countsReady && !executiveOnly,
  );
  // "1 new · 116 open": the default view (today) and every open inquiry of the
  // same scope over all dates (`backlog.open_total`, the same answer). One number alone
  // hid most of the open work.
  const reviewCount = queueBadgeText(
    reviewPage?.counts?.work ?? null,
    reviewPage?.backlog?.open_total ?? null,
  );
  // Own feedback items with a reply or status change since last opened (polled, no push).
  const { data: unreadFeedback } = useFeedbackUnreadCount();
  // Cases back for me: a customer I confirmed was matched again, or a waiting case is due.
  const { data: attention } = useMyAttention(countsReady && FEATURES.backForYou && !executiveOnly);
  const trainingNew = useTrainingSummary(FEATURES.training && !executiveOnly).showNewBadge;
  const backCount = attention
    ? (attention.back_after_customer ?? 0) +
      (attention.back_from_waiting ?? 0) +
      (attention.customer_chasing ?? 0)
    : null;
  const where: Where = {
    pathname: location.pathname,
    search: location.search,
    hash: location.hash,
  };
  // Entries that need a capability (rules, triage) are hidden without it.
  const sections = visibleSections(me);

  return (
    <div className="bg-bg flex min-h-screen">
      {/* pm9 (17 Oct 2026, UX M9): the first stop of the keyboard skips the rail and the bar.
          Hidden until it has the focus. */}
      <TextLink
        href="#content"
        className="bg-surface sr-only z-50 rounded-md px-3 py-2 text-sm font-semibold focus:not-sr-only focus:fixed focus:top-2 focus:left-2 print:hidden"
        data-skip-link=""
      >
        {SKIP_TO_CONTENT}
      </TextLink>
      {/* Fixed Norican Blue in both themes, so always the white logo and white text
          (Brand Guidelines 3.0 p.21: text only in Norican Blue, Accent Mid Blue or White). */}
      {/* The rail and the top bar are for the screen: a printed page (the documentation,
          a help topic) holds the content only. */}
      <NavRail
        me={me}
        sections={sections}
        where={where}
        badges={{ reviewCount, backCount, unreadFeedback, trainingNew }}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="border-border bg-surface sticky top-0 z-10 flex min-w-0 items-center gap-2 border-b px-4 py-3 md:gap-4 md:px-6 print:hidden">
          {/* Below md the sidebar is hidden, so the mark moves into the header (theme-aware). */}
          <Link
            to={homeHref(me)}
            aria-label={PRODUCT_NAME}
            className="flex shrink-0 items-center gap-2 no-underline md:hidden"
          >
            {/* Secondary logo below the primary's 35 px minimum (guide p.17); 20 px box =
                11 px of artwork. */}
            <NoricanLogo kind="secondary" height={20} />
            <span className="text-fg hidden text-sm font-bold whitespace-nowrap min-[400px]:inline">
              {PRODUCT_SHORT}
            </span>
          </Link>
          {/* font-display font-normal leading-display: Turbine Regular at 96 % line height
              for titles (guide p.23). Workaround
              until the DS heading variants use the display face (ds-gap G-5). */}
          <Typography
            as="h1"
            variant="heading-5"
            className="font-display leading-display hidden font-normal whitespace-nowrap md:block"
          >
            Review workbench
          </Typography>
          {/* Small screens only: at md and above the sidebar navigates. */}
          <div className="min-w-0 md:hidden">
            <MobileNav
              sections={sections}
              pathname={where}
              reviewCount={reviewCount}
              backCount={backCount}
              trainingNew={trainingNew}
            />
          </div>
          <div className="ml-auto flex shrink-0 items-center gap-1 md:gap-3">
            <ShadowModeBadge className="hidden lg:inline-flex" />
            <ShadowModeBadge label="Shadow mode" className="hidden md:inline-flex lg:hidden" />
            <ShadowModeBadge iconOnly className="md:hidden" />
            <GiveFeedbackButton
              options={shellFeedbackOptions(where.pathname)}
              className="hidden md:inline-flex"
            />
            <span className="hidden md:inline-flex">
              <ThemeToggle />
            </span>
            <UserChip me={me} />
          </div>
        </header>

        <main
          id="content"
          tabIndex={-1}
          className="flex min-w-0 flex-1 flex-col gap-4 overflow-x-clip p-4 md:p-6"
        >
          <EngineStatusBanner />
          {/* A page that loads on demand (`App.tsx`) waits here: the rail and the bar stay. */}
          <Suspense fallback={<PageLoading />}>
            <Outlet context={{ me }} />
          </Suspense>
        </main>
      </div>
    </div>
  );
}
